import useIsMobile from '../../lib/useIsMobile';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  muted: '#64748B',
  border: '#E2E8F0',
};

function Section({ title, children, isMobile }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ color: C.navy, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: '0 0 14px' }}>{title}</h2>
      <div style={{ color: C.muted, fontSize: isMobile ? 14 : 15, lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

export default function PrivacyPage() {
  const isMobile = useIsMobile();

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <MarketingNav />

      <section style={{ textAlign: 'center', padding: isMobile ? '60px 20px 40px' : '100px 24px 60px', maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ color: C.navy, fontSize: isMobile ? 28 : 44, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.2 }}>Privacy Policy</h1>
        <p style={{ color: C.muted, fontSize: isMobile ? 14 : 16, margin: 0 }}>Last updated: March 2026</p>
      </section>

      <section style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '0 20px 60px' : '0 24px 80px' }}>

        <Section title="1. Information We Collect" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            ClearSeg ("we," "our," or "us") collects information that you provide directly when using our services, including:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 24 }}>
            <li><strong style={{ color: C.navy }}>Personal Information:</strong> Name, email address, phone number, and mailing address.</li>
            <li><strong style={{ color: C.navy }}>Financial &amp; Property Information:</strong> Property purchase price, acquisition date, tax-related data, and property details necessary for your cost segregation study.</li>
            <li><strong style={{ color: C.navy }}>Documents &amp; Media:</strong> Property photos, closing documents, purchase agreements, and other files you upload to our platform.</li>
            <li><strong style={{ color: C.navy }}>Account Information:</strong> Login credentials and profile details when you create an account.</li>
            <li><strong style={{ color: C.navy }}>Usage Data:</strong> IP address, browser type, device information, pages visited, and interactions with our site collected automatically through cookies and analytics tools.</li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>We use the information we collect to:</p>
          <ul style={{ margin: 0, paddingLeft: 24 }}>
            <li>Prepare and deliver your cost segregation study.</li>
            <li>Process payments and communicate about your order.</li>
            <li>Send transactional emails (order confirmations, study delivery, account updates).</li>
            <li>Improve our services, website, and user experience.</li>
            <li>Analyze site traffic and usage patterns.</li>
            <li>Comply with legal obligations and enforce our terms of service.</li>
            <li>Respond to your inquiries and provide customer support.</li>
          </ul>
        </Section>

        <Section title="3. Information Sharing and Disclosure" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            We do not sell, rent, or trade your personal information. We may share your information with:
          </p>
          <ul style={{ margin: 0, paddingLeft: 24 }}>
            <li><strong style={{ color: C.navy }}>Service Providers:</strong> Third-party vendors that help us operate our business, including payment processing (Stripe), email delivery (Resend), data hosting (Railway/PostgreSQL), and file storage (Cloudflare R2).</li>
            <li><strong style={{ color: C.navy }}>Licensed Engineers:</strong> Professional Engineers (PEs) who review and certify your cost segregation study.</li>
            <li><strong style={{ color: C.navy }}>Your CPA or Tax Professional:</strong> When you direct us to deliver your completed study to your designated tax advisor.</li>
            <li><strong style={{ color: C.navy }}>Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request.</li>
          </ul>
        </Section>

        <Section title="4. Data Storage and Security" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            Your data is stored on secure infrastructure provided by Railway (PostgreSQL databases) and Cloudflare R2 (document and file storage). We implement industry-standard security measures including encryption in transit (TLS/SSL), access controls, and regular security reviews to protect your information.
          </p>
          <p style={{ margin: 0 }}>
            While we strive to protect your personal information, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your data.
          </p>
        </Section>

        <Section title="5. Payment Processing" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            All payment transactions are processed through Stripe, a PCI-DSS Level 1 certified payment processor. ClearSeg does not store your credit card number, expiration date, or CVV on our servers. Payment information is transmitted directly to Stripe via their secure, encrypted connection. Please refer to{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, textDecoration: 'none' }}>Stripe's Privacy Policy</a>{' '}
            for details on how they handle your payment data.
          </p>
        </Section>

        <Section title="6. Cookies and Analytics" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            We use cookies and similar tracking technologies to analyze site traffic, remember your preferences, and improve your experience. Specifically:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 24 }}>
            <li><strong style={{ color: C.navy }}>Essential Cookies:</strong> Required for basic site functionality (e.g., authentication, session management).</li>
            <li><strong style={{ color: C.navy }}>Analytics Cookies:</strong> Used to understand how visitors interact with our site (e.g., Google Analytics). These collect anonymized usage data.</li>
            <li><strong style={{ color: C.navy }}>Preference Cookies:</strong> Used to remember your choices, such as cookie consent status.</li>
          </ul>
          <p style={{ margin: 0 }}>
            You can manage or disable cookies through your browser settings. Disabling certain cookies may affect the functionality of our site.
          </p>
        </Section>

        <Section title="7. Your Rights" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>You have the right to:</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 24 }}>
            <li><strong style={{ color: C.navy }}>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong style={{ color: C.navy }}>Correction:</strong> Request that we correct any inaccurate or incomplete information.</li>
            <li><strong style={{ color: C.navy }}>Deletion:</strong> Request that we delete your personal information, subject to legal retention requirements.</li>
            <li><strong style={{ color: C.navy }}>Opt-Out:</strong> Unsubscribe from marketing communications at any time.</li>
          </ul>
          <p style={{ margin: 0 }}>
            To exercise any of these rights, contact us at <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a>. We will respond to your request within 30 days.
          </p>
        </Section>

        <Section title="8. Data Retention" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            We retain your personal information for as long as necessary to provide our services and fulfill the purposes described in this policy. Cost segregation study data and related documents are retained for a minimum of seven (7) years to comply with IRS record-keeping requirements. After this period, or upon a valid deletion request (where no legal obligation to retain exists), your data will be securely deleted.
          </p>
        </Section>

        <Section title="9. Children's Privacy" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us at <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a> and we will promptly delete it.
          </p>
        </Section>

        <Section title="10. Changes to This Policy" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            We may update this Privacy Policy from time to time. When we make material changes, we will notify you by updating the "Last updated" date at the top of this page and, where appropriate, providing additional notice via email. Your continued use of our services after any changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="11. Contact Us" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div style={{ marginTop: 16, padding: '20px 24px', background: C.white, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <p style={{ color: C.navy, fontWeight: 600, margin: '0 0 8px' }}>ClearSeg LLC</p>
            <p style={{ color: C.muted, margin: '0 0 4px', fontSize: 14 }}>1970 Swarthmore Ave, Ste 6B, Lakewood, NJ 08701</p>
            <p style={{ color: C.muted, margin: '0 0 4px', fontSize: 14 }}>
              Email: <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a>
            </p>
            <p style={{ color: C.muted, margin: '0 0 4px', fontSize: 14 }}>
              Phone: <a href="tel:+16465331623" style={{ color: C.accent, textDecoration: 'none' }}>(646) 533-1623</a>
            </p>
          </div>
        </Section>

      </section>

      <Footer />
    </div>
  );
}
